import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Smartphone, Wallet, QrCode, ShieldCheck, Loader2 } from "lucide-react";
import TutorialHelp from "@/components/TutorialHelp";

const PROVIDERS = [
  { value: "khalti", name: "Khalti", brand: "#5C2D91", icon: Wallet, sub: "Khalti wallet, mobile banking" },
  { value: "esewa", name: "eSewa", brand: "#60BB46", icon: Smartphone, sub: "eSewa wallet (Nepal's largest)" },
  { value: "manual_qr", name: "QR Payment", brand: "#0A2540", icon: QrCode, sub: "Static QR — admin verifies manually" },
];

export default function PaymentCheckout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const context_type = params.get("context_type") || "guidance";
  const context_id = params.get("context_id") || "";
  const [provider, setProvider] = useState("khalti");
  const [coupon, setCoupon] = useState("");
  const [initiating, setInitiating] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState("");

  const onPay = async () => {
    if (initiating) return;
    setInitiating(true);
    setOverlayMessage(`Connecting to ${PROVIDERS.find(p => p.value === provider).name}…`);
    // Cache the payment context so /payments/return can route the user back to the right place
    try {
      sessionStorage.setItem("kb_payment_context", JSON.stringify({ context_type, context_id }));
    } catch { /* ignore */ }
    try {
      const r = await api.post("/payments/initiate", {
        context_type, context_id, provider,
        return_url: window.location.origin + "/payments/return",
        coupon_code: coupon || undefined,
      });
      const p = r.data;
      if (provider === "khalti" && p.payment_url) {
        setOverlayMessage("Redirecting to Khalti checkout…");
        window.location.href = p.payment_url;
        return; // keep overlay until navigation
      } else if (provider === "esewa" && p.esewa_payload) {
        setOverlayMessage("Redirecting to eSewa checkout…");
        // build form and submit
        const form = document.createElement("form");
        form.method = "POST";
        form.action = p.esewa_payload.form_url;
        for (const key of ["amount", "tax_amount", "product_service_charge", "product_delivery_charge",
                          "total_amount", "transaction_uuid", "product_code",
                          "success_url", "failure_url", "signed_field_names", "signature"]) {
          const input = document.createElement("input");
          input.type = "hidden"; input.name = key; input.value = p.esewa_payload[key];
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        return; // keep overlay until navigation
      } else if (provider === "manual_qr") {
        navigate(`/payments/return?provider=manual_qr&purchase_order_id=${p.purchase_order_id}`);
      } else {
        setInitiating(false);
        toast.error("Could not start payment");
      }
    } catch (e) {
      setInitiating(false);
      setOverlayMessage("");
      toast.error(e?.response?.data?.detail || "Couldn't initiate payment");
    }
  };

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="payment-checkout-page">
      {initiating && (
        <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center" data-testid="payment-initiating-overlay">
          <div className="text-center max-w-sm px-6">
            <Loader2 className="w-10 h-10 text-kb-primary mx-auto animate-spin" strokeWidth={1.5} />
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-5">SECURE CHECKOUT</div>
            <div className="font-serif text-2xl text-kb-ink mt-2">{overlayMessage || "Initiating payment…"}</div>
            <div className="text-sm text-kb-ink-muted mt-3">Please don't refresh or close this tab. You'll be redirected to complete payment.</div>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-6 py-14">
        <Link to="/dashboard" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK
        </Link>
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-6">CHECKOUT</div>
        <h1 className="font-serif text-3xl text-kb-ink mt-1">Choose your <em className="italic text-kb-primary">payment method</em>.</h1>
        <p className="text-sm text-kb-ink-muted mt-2">Paying for: <span className="font-mono text-kb-ink">{context_type} · {context_id}</span></p>

        <div className="mt-6">
          <TutorialHelp tutorialKey="payments" />
        </div>

        <div className="mt-8 space-y-3" data-testid="payment-provider-list">
          {PROVIDERS.map((p) => {
            const active = provider === p.value;
            return (
              <button key={p.value} onClick={() => setProvider(p.value)} disabled={initiating}
                className={`w-full text-left border rounded-sm p-4 flex items-center gap-4 ${active ? "border-kb-primary bg-kb-primary/5" : "border-slate-200 hover:border-kb-primary/50"} disabled:opacity-50`}
                data-testid={`provider-${p.value}`}>
                <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: p.brand + "15", color: p.brand }}>
                  <p.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-kb-ink">{p.name}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-0.5">{p.sub}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 ${active ? "border-kb-primary bg-kb-primary" : "border-slate-300"}`}>
                  {active && <div className="w-2 h-2 bg-white rounded-full m-auto mt-1" />}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 kb-card p-5">
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Coupon code (optional)</label>
          <input className="kb-input mt-1.5" placeholder="e.g. NEPAL2026" value={coupon} disabled={initiating}
            onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="payment-coupon-input" />
        </div>

        <button onClick={onPay} disabled={initiating} className="kb-btn-primary w-full justify-center mt-8 text-base py-4" data-testid="payment-pay-btn">
          {initiating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
          ) : (
            <><ShieldCheck className="w-4 h-4" /> Pay securely with {PROVIDERS.find(p => p.value === provider).name}</>
          )}
        </button>
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-4 text-center">
          SANDBOX MODE · TEST CREDENTIALS · NO REAL MONEY MOVED
        </div>
      </div>
    </div>
  );
}
